import axios from '@/lib/axios'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { useDebounceFn } from '@vueuse/core'
import { MaybeRef, computed, ref, unref } from 'vue'

import useAuthUser from '@/composables/useAuthUser'

import type { CustomAxiosRequestConfig } from '@/interfaces/customAxiosRequestConfig'

import type {
	CancelSubscriptionResponse,
	CouponPreviewContext,
	CouponPreviewResponse,
	CreateSubscriptionPayload,
	ScheduledSwapResponse,
	Subscription,
	SubscriptionActionResponse,
	SubscriptionCredit,
	SubscriptionIndexResponse,
	SubscriptionPlanFeatureLite,
	SubscriptionPlanSummary,
	SubscriptionStatus,
	SwapPreviewResponse,
	SwapSubscriptionPayload,
} from '@/modules/plans/interfaces/subscription'

interface Props {
	immediate?: boolean
	companyId?: MaybeRef<number | undefined>
}

interface RawPlanFeature {
	code: string
	name: string
	kind: 'limit' | 'boolean'
	unit: string | null
	pivot?: { value: string | Record<string, unknown> }
	value?: string | Record<string, unknown>
}

interface RawPlanSummary {
	id: number
	code: string
	name: string
	description: string | null
	priceMxn: string | number
	billingInterval: 'monthly' | 'annual'
	gracePeriodDays: number
	isActive: boolean
	isDefault: boolean
	isPublic?: boolean
	sortOrder: number
	features?: RawPlanFeature[]
}

interface RawCredit {
	id: number
	limit: string | number
	available: string | number
	usage: string | number
	availablePercentage: string | number
	deadline: number
	overdraft: string | number
	creditableName?: string
	creditableLogo?: string | null
	lastSyncedAt: string | null
}

interface RawPlan {
	id: number
	code: string
	name: string
}

interface RawSubscription {
	id: number
	companyId: number
	planId: number
	plan?: RawPlan
	status: SubscriptionStatus
	priceMxnSnapshot: string | number
	billingInterval: 'monthly' | 'annual'
	currentPeriodStart?: string | null
	currentPeriodEnd: string
	trialEndsAt: string | null
	cancelledAt: string | null
	cancelAtPeriodEnd: boolean
	pendingPlanId: number | null
	pendingPlan?: RawPlan
	createdAt: string
	updatedAt: string
}

interface RawPendingCharges {
	totalMxn: string | number
	count: number
}

interface RawSubscriptionResponse {
	subscription: RawSubscription | null
	credit: RawCredit | null
	plans: RawPlanSummary[]
	pendingCharges?: RawPendingCharges | null
}

const toNumber = (
	v: string | number | null | undefined,
	fallback = 0
): number => {
	if (v === null || v === undefined) return fallback
	if (typeof v === 'number') return v
	const n = parseFloat(v)
	return Number.isFinite(n) ? n : fallback
}

const normalizeFeature = (f: RawPlanFeature): SubscriptionPlanFeatureLite => {
	const raw = f.pivot?.value ?? f.value ?? '{}'
	return {
		code: f.code,
		name: f.name,
		kind: f.kind,
		unit: f.unit,
		value: typeof raw === 'string' ? raw : JSON.stringify(raw),
	}
}

const normalizePlan = (p: RawPlanSummary): SubscriptionPlanSummary => ({
	id: p.id,
	code: p.code,
	name: p.name,
	description: p.description,
	priceMxn: toNumber(p.priceMxn),
	billingInterval: p.billingInterval,
	gracePeriodDays: p.gracePeriodDays,
	isActive: p.isActive,
	isDefault: p.isDefault,
	// Público si el backend no lo manda: mientras la API no despliegue el campo,
	// el catálogo que llega ya viene filtrado y todo lo que trae es contratable.
	isPublic: p.isPublic ?? true,
	sortOrder: p.sortOrder,
	features: (p.features ?? []).map(normalizeFeature),
})

const normalizeSubscription = (
	s: RawSubscription | null
): Subscription | null => {
	if (!s) return null
	return {
		id: s.id,
		companyId: s.companyId,
		planId: s.planId,
		planCode: s.plan?.code ?? '',
		planName: s.plan?.name ?? '',
		status: s.status,
		priceMxn: toNumber(s.priceMxnSnapshot),
		billingInterval: s.billingInterval,
		currentPeriodStart: s.currentPeriodStart ?? null,
		currentPeriodEnd: s.currentPeriodEnd,
		trialEndsAt: s.trialEndsAt,
		cancelledAt: s.cancelledAt,
		cancelAtPeriodEnd: s.cancelAtPeriodEnd,
		pendingPlanId: s.pendingPlanId,
		pendingPlan: s.pendingPlan,
		createdAt: s.createdAt,
		updatedAt: s.updatedAt,
	}
}

const normalizeCredit = (c: RawCredit | null): SubscriptionCredit | null => {
	if (!c) return null
	return {
		id: c.id,
		limit: toNumber(c.limit),
		available: toNumber(c.available),
		usage: toNumber(c.usage),
		availablePercentage: toNumber(c.availablePercentage),
		deadline: c.deadline,
		overdraft: toNumber(c.overdraft),
		creditableName: c.creditableName ?? '',
		creditableLogo: c.creditableLogo ?? null,
		lastSyncedAt: c.lastSyncedAt,
	}
}

const baseUrl = (companyId: number | undefined): string =>
	`/api/companies/${companyId}/subscriptions`

const normalizeResponse = (
	data: RawSubscriptionResponse | null | undefined
): SubscriptionIndexResponse => ({
	subscription: normalizeSubscription(data?.subscription ?? null),
	credit: normalizeCredit(data?.credit ?? null),
	plans: (data?.plans ?? [])
		.map(normalizePlan)
		.sort((a, b) => a.sortOrder - b.sortOrder),
	pendingCharges:
		data?.pendingCharges ?
			{
				totalMxn: toNumber(data.pendingCharges.totalMxn),
				count: data.pendingCharges.count,
			}
		:	null,
})

const getSubscriptionIndex = async (
	companyId: number | undefined
): Promise<SubscriptionIndexResponse> => {
	if (!companyId) {
		return { subscription: null, credit: null, plans: [], pendingCharges: null }
	}

	const config = { errorHandling: false } as CustomAxiosRequestConfig

	const [{ data: activeData }, { data: indexData }] = await Promise.all([
		axios.get<RawSubscriptionResponse>(`${baseUrl(companyId)}/active`, config),
		axios.get<RawSubscriptionResponse>(baseUrl(companyId), config),
	])

	const activeNormalized = normalizeResponse(activeData)
	const indexNormalized = normalizeResponse(indexData)

	return {
		subscription: activeNormalized.subscription,
		credit: activeNormalized.credit ?? indexNormalized.credit,
		plans:
			indexNormalized.plans.length ?
				indexNormalized.plans
			:	activeNormalized.plans,
		pendingCharges: activeNormalized.pendingCharges,
	}
}

type SubscriptionApiResponse =
	| { data: Subscription; redirectUrl?: string }
	| { subscription: Subscription; redirectUrl?: string }
	| Subscription
	| ScheduledSwapResponse

const isScheduledSwap = (data: unknown): data is ScheduledSwapResponse => {
	return typeof data === 'object' && data !== null && 'swapType' in data
}

const unwrap = (data: SubscriptionApiResponse): SubscriptionActionResponse => {
	if ('subscription' in (data as object)) {
		const r = data as { subscription: Subscription; redirectUrl?: string }
		return { subscription: r.subscription, redirectUrl: r.redirectUrl }
	}
	if ('data' in (data as object)) {
		const r = data as { data: Subscription; redirectUrl?: string }
		return { subscription: r.data, redirectUrl: r.redirectUrl }
	}
	return { subscription: data as Subscription }
}

const createSubscription = async (
	payload: CreateSubscriptionPayload,
	companyId: number | undefined
): Promise<SubscriptionActionResponse> => {
	if (!companyId) throw new Error('companyId requerido')
	const { paymentMethod, planCode, cardId, couponCode } = payload
	const { data } = await axios.post<SubscriptionApiResponse>(baseUrl(companyId), {
		plan_code: planCode,
		method: paymentMethod === 'card' ? 'openpay' : paymentMethod,
		card_id: cardId,
		couponCode,
	})
	return unwrap(data)
}

const subscriptionAction = async (
	id: number,
	action: 'pay' | 'resume',
	companyId: number | undefined
): Promise<SubscriptionActionResponse> => {
	if (!companyId) throw new Error('companyId requerido')
	const { data } = await axios.post<SubscriptionApiResponse>(
		`${baseUrl(companyId)}/${id}/${action}`
	)
	return unwrap(data)
}

const cancelSubscription = async (
	id: number,
	companyId: number | undefined
): Promise<CancelSubscriptionResponse> => {
	if (!companyId) throw new Error('companyId requerido')
	const { data } = await axios.post<CancelSubscriptionResponse>(
		`${baseUrl(companyId)}/${id}/cancel`
	)
	return data
}

const getSubscriptionShow = async (
	companyId: number | undefined,
	id: number
): Promise<Subscription | null> => {
	if (!companyId) return null
	const { data } = await axios.get<{ data: Subscription } | Subscription>(
		`${baseUrl(companyId)}/${id}`
	)
	if (data && typeof data === 'object' && 'data' in data) {
		return (data as { data: Subscription }).data
	}
	return data as Subscription
}

const swapSubscription = async (
	id: number,
	payload: SwapSubscriptionPayload,
	companyId: number | undefined
): Promise<SubscriptionActionResponse | ScheduledSwapResponse> => {
	if (!companyId) throw new Error('companyId requerido')
	const { paymentMethod, planCode, cardId, deviceSessionId, redirectUrl, couponCode } = payload
	const { data } = await axios.post<SubscriptionApiResponse>(
		`${baseUrl(companyId)}/${id}/swap`,
		{
			plan_code: planCode,
			method: paymentMethod === 'card' ? 'openpay' : paymentMethod,
			card_id: cardId,
			device_session_id: deviceSessionId,
			redirect_url: redirectUrl,
			couponCode,
		}
	)
	if (isScheduledSwap(data)) {
		return data
	}
	return unwrap(data)
}

const getSwapPreview = async (
	id: number,
	planCode: string,
	companyId: number | undefined
) => {
	if (!companyId) throw new Error('companyId requerido')
	const { data } = await axios.get<SwapPreviewResponse>(
		`${baseUrl(companyId)}/${id}/swap/preview`,
		// Keep preview failures contained in the modal instead of letting the
		// global redirect-on-error interceptor bounce the whole app.
		{
			params: { plan_code: planCode },
			errorHandling: false,
		} as CustomAxiosRequestConfig
	)
	return data
}

const getCouponPreview = async (
	context: CouponPreviewContext,
	couponCode: string,
	companyId: number | undefined,
	planCode?: string
) => {
	if (!companyId) throw new Error('companyId requerido')
	const { data } = await axios.get<CouponPreviewResponse>(
		`${baseUrl(companyId)}/coupon-preview`,
		// Rejected coupon answers 200 with valid:false, not an HTTP error — this
		// is only here for genuinely malformed requests (missing plan_code, etc).
		{
			params: { context, coupon_code: couponCode, plan_code: planCode },
			errorHandling: false,
		} as CustomAxiosRequestConfig
	)
	return data
}

const useCompanySubscriptions = (props?: Props) => {
	const { immediate = true, companyId: companyIdProp } = props || {}
	const { user } = useAuthUser()
	const queryClient = useQueryClient()

	const companyId = computed<number | undefined>(
		() => unref(companyIdProp) ?? user.value?.companyId
	)

	const invalidate = () =>
		queryClient.invalidateQueries({ queryKey: ['subscriptions'] })

	const initialData: SubscriptionIndexResponse = {
		subscription: null,
		credit: null,
		plans: [],
		pendingCharges: null,
	}

	const { data, isLoading, isInitialLoading, isFetching, isError, error, refetch, status } =
		useQuery<SubscriptionIndexResponse>(
			['subscriptions', computed(() => companyId.value ?? null)],
			() => getSubscriptionIndex(companyId.value),
			{
				enabled: immediate,
				retry: false,
				refetchOnWindowFocus: false,
				initialData,
			}
		)

	const createMutation = useMutation({
		mutationFn: (payload: CreateSubscriptionPayload) =>
			createSubscription(payload, companyId.value),
		onSuccess: invalidate,
	})

	const cancelMutation = useMutation({
		mutationFn: (id: number) => cancelSubscription(id, companyId.value),
		onSuccess: invalidate,
	})

	const payMutation = useMutation({
		mutationFn: (id: number) => subscriptionAction(id, 'pay', companyId.value),
		onSuccess: invalidate,
	})

	const resumeMutation = useMutation({
		mutationFn: (id: number) =>
			subscriptionAction(id, 'resume', companyId.value),
		onSuccess: invalidate,
	})

	const swapMutation = useMutation({
		mutationFn: ({
			id,
			payload,
		}: {
			id: number
			payload: SwapSubscriptionPayload
		}) => swapSubscription(id, payload, companyId.value),
		onSuccess: invalidate,
	})

	const useSwapPreview = (
		subscriptionId: MaybeRef<number | undefined>,
		planCode: MaybeRef<string | undefined>
	) => {
		return useQuery<SwapPreviewResponse>(
			[
				'swap-preview',
				computed(() => unref(companyId) ?? null),
				computed(() => unref(subscriptionId) ?? null),
				computed(() => unref(planCode) ?? null),
			],
			() =>
				getSwapPreview(
					unref(subscriptionId)!,
					unref(planCode)!,
					unref(companyId)
				),
			{
				enabled: computed(
					() =>
						!!unref(companyId) &&
						!!unref(subscriptionId) &&
						!!unref(planCode) &&
						unref(planCode) !== ''
				),
				retry: false,
				refetchOnWindowFocus: false,
			}
		)
	}

	const useCouponPreview = (
		context: MaybeRef<CouponPreviewContext>,
		couponCode: MaybeRef<string | undefined>,
		planCode?: MaybeRef<string | undefined>
	) => {
		return useQuery<CouponPreviewResponse>(
			[
				'coupon-preview',
				computed(() => unref(companyId) ?? null),
				computed(() => unref(context)),
				computed(() => unref(planCode) ?? null),
				computed(() => unref(couponCode) ?? null),
			],
			() =>
				getCouponPreview(
					unref(context),
					unref(couponCode)!,
					unref(companyId),
					unref(planCode)
				),
			{
				// Sin piso de longitud: el backend no exige un mínimo, así que un
				// código de 1-2 caracteres también debe validar (y fallar) en vez
				// de quedarse en silencio hasta llegar a 3.
				enabled: computed(() => !!unref(companyId) && !!unref(couponCode)),
				retry: false,
				refetchOnWindowFocus: false,
				// Mismo contexto + plan + cupón (p. ej. lo borran y lo vuelven a
				// escribir igual) sirve del cache en vez de repetir la petición.
				staleTime: 5 * 60 * 1000, // 5 minutos
			}
		)
	}

	/**
	 * Combines swap/preview + coupon-preview into the one thing a checkout
	 * screen actually needs: is this a swap or a fresh subscribe (drives which
	 * coupon context to quote against), and the coupon's own valid/error/
	 * discount state. Deriving `isSwap` from this factory's own `data` — rather
	 * than having each caller compute it separately — is what keeps
	 * PlanGateModal and CompanySubscriptionsPage from drifting apart on this;
	 * they diverged once already (one modal quoted a swap's coupon against the
	 * plan's flat price instead of the prorated diff) precisely because each
	 * had its own copy of this logic.
	 */
	const useCouponPricing = (
		planCode: MaybeRef<string | undefined>,
		couponCode: MaybeRef<string | undefined>
	) => {
		const isSwap = computed(() => !!data.value?.subscription)
		const subscriptionId = computed(() => data.value?.subscription?.id)

		const {
			data: swapPreview,
			isLoading: previewLoading,
			isError: isPreviewError,
			error: previewErrorRaw,
		} = useSwapPreview(subscriptionId, planCode)

		const previewError = computed(() => {
			if (!previewErrorRaw.value) return ''
			const err = previewErrorRaw.value as { response?: { data?: { message?: string } } }
			return err?.response?.data?.message ?? 'No se pudo obtener el preview del cambio'
		})

		const couponContext = computed<CouponPreviewContext>(
			() => (isSwap.value ? 'swap' : 'subscribe')
		)

		const {
			data: couponPreviewRaw,
			isFetching: couponValidating,
			isError: isCouponRequestError,
		} = useCouponPreview(couponContext, couponCode, planCode)

		const couponPreview = computed(() =>
			couponPreviewRaw.value?.valid ? couponPreviewRaw.value : null
		)
		const couponHasError = computed(
			() =>
				!!unref(couponCode) &&
				(isCouponRequestError.value || couponPreviewRaw.value?.valid === false)
		)
		const couponErrorText = computed(
			() => couponPreviewRaw.value?.message ?? 'No se pudo validar el cupón'
		)
		const couponIsValidating = computed(
			() => !!unref(couponCode) && couponValidating.value
		)
		const couponDiscountAmount = computed(() => couponPreview.value?.discountAmount ?? 0)

		return {
			isSwap,
			swapPreview,
			previewLoading,
			isPreviewError,
			previewError,
			couponPreview,
			couponHasError,
			couponErrorText,
			couponIsValidating,
			couponDiscountAmount,
		}
	}

	return {
		data,
		isLoading,
		isInitialLoading,
		isFetching,
		isError,
		error,
		refetch,
		status,
		companyId,

		createSubscription: createMutation.mutateAsync,
		createLoading: createMutation.isLoading,

		cancelSubscription: cancelMutation.mutateAsync,
		cancelLoading: cancelMutation.isLoading,

		paySubscription: payMutation.mutateAsync,
		payLoading: payMutation.isLoading,

		resumeSubscription: resumeMutation.mutateAsync,
		resumeLoading: resumeMutation.isLoading,

		swapSubscription: (id: number, payload: SwapSubscriptionPayload) =>
			swapMutation.mutateAsync({ id, payload }),
		swapLoading: swapMutation.isLoading,

		useSwapPreview,
		useCouponPreview,
		useCouponPricing,
	}
}

/**
 * Coupon-code input state shared by the checkout screens: the visible
 * v-model plus a "committed" value that only moves once the customer has
 * settled on a code, which is what actually drives the preview request.
 *
 * A pasted/dropped code commits immediately (native InputEvent.inputType —
 * 'insertFromPaste'/'insertFromDrop' — fires after the value updates, unlike
 * the 'paste' event which fires before); typed keystrokes commit after a
 * pause so we're not firing a request per character.
 */
const useCouponCodeInput = () => {
	const couponCode = ref('')
	const couponCodeCommitted = ref('')

	const commit = () => {
		couponCodeCommitted.value = couponCode.value.trim().toUpperCase()
	}
	const commitDebounced = useDebounceFn(commit, 500)

	const onCouponInput = (event: Event) => {
		const value = (event.target as HTMLInputElement).value

		if (!value.trim()) {
			couponCodeCommitted.value = ''
			return
		}

		const inputType = (event as InputEvent).inputType
		if (inputType === 'insertFromPaste' || inputType === 'insertFromDrop') {
			commit()
		} else {
			commitDebounced()
		}
	}

	const resetCoupon = () => {
		couponCode.value = ''
		couponCodeCommitted.value = ''
	}

	return { couponCode, couponCodeCommitted, onCouponInput, resetCoupon }
}

export default useCompanySubscriptions
export { getSubscriptionShow, useCouponCodeInput }
